import { ComponentBase } from "../../core/ComponentBase.js";
import { marked } from "https://cdn.jsdelivr.net/npm/marked/lib/marked.esm.js";

// ── Extension → category mapping ─────────────────────────────────────────────
const EXT_MAP = {
    // code
    py:'code', js:'code', ts:'code', jsx:'code', tsx:'code', sh:'code',
    bash:'code', json:'code', yaml:'code', yml:'code', toml:'code',
    env:'code', sql:'code', css:'code', xml:'code', html:'html',
    rs:'code', go:'code', java:'code', c:'code', cpp:'code', rb:'code',
    php:'code', swift:'code', kt:'code', lua:'code', r:'code',
    // text
    txt:'text', log:'text',
    // markdown
    md:'markdown', mdx:'markdown',
    // image
    png:'image', jpg:'image', jpeg:'image', gif:'image', webp:'image', svg:'image',
    // pdf
    pdf:'pdf',
    // office — modern Open XML (rendered client-side by JS libraries)
    docx:'docx',
    xlsx:'xlsx', xls:'xlsx', csv:'xlsx',
    // office — no reliable lightweight client-side renderer; show graceful download prompt
    doc:'office-fallback', ppt:'office-fallback', pptx:'office-fallback', odp:'office-fallback',
    // media
    mp4:'video', webm:'video', mov:'video',
    mp3:'audio', wav:'audio', ogg:'audio', m4a:'audio',
    // sandboxed html
    htm:'html',
};

const MAX_TEXT_BYTES = 512 * 1024; // 512 KB — truncate previews beyond this

// ── Office renderer loaders (lazy, cached) ───────────────────────────────────
let _docxPreviewLoaded = false;
let _docxPreviewLoading = null;
async function loadDocxPreview() {
    if (_docxPreviewLoaded) return true;
    if (_docxPreviewLoading) return _docxPreviewLoading;
    _docxPreviewLoading = new Promise((resolve) => {
        // docx-preview's UMD bundle reads window.JSZip as a global dependency.
        // Load JSZip first, then docx-preview, otherwise renderAsync crashes
        // with "Cannot read properties of undefined (reading 'loadAsync')".
        const jszip = document.createElement('script');
        jszip.src = 'https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js';
        jszip.onload = () => {
            const s = document.createElement('script');
            s.src = 'https://cdn.jsdelivr.net/npm/docx-preview@0.3.7/dist/docx-preview.min.js';
            s.onload = () => { _docxPreviewLoaded = true; resolve(true); };
            s.onerror = () => resolve(false);
            document.head.appendChild(s);
        };
        jszip.onerror = () => resolve(false);
        document.head.appendChild(jszip);
    });
    return _docxPreviewLoading;
}

let _sheetjsLoaded = false;
let _sheetjsLoading = null;
async function loadSheetJS() {
    if (_sheetjsLoaded) return true;
    if (_sheetjsLoading) return _sheetjsLoading;
    _sheetjsLoading = new Promise((resolve) => {
        const s = document.createElement('script');
        s.src = 'https://cdn.sheetjs.com/xlsx-latest/package/dist/xlsx.full.min.js';
        s.onload = () => { _sheetjsLoaded = true; resolve(true); };
        s.onerror = () => resolve(false);
        document.head.appendChild(s);
    });
    return _sheetjsLoading;
}

// ── Syntax highlight loader — theme-aware (lazy, cached) ─────────────────────
let _hljs = null;
let _hljsLoading = null;

const HLJS_LIGHT   = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/github.min.css';
const HLJS_DARK    = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/github-dark.min.css';
const HLJS_LINK_ID = 'fpm-hljs-theme';

/** Returns true when the active data-bs-theme is a dark-background variant. */
function _hljsIsDark() {
    const t = (document.documentElement.getAttribute('data-bs-theme') || '').toLowerCase();
    return /dark|noir|midnight|ocean|forest|vibrant/.test(t);
}

/** Inserts or updates the hljs <link> to match the current app theme. */
function _applyHljsTheme() {
    const href = _hljsIsDark() ? HLJS_DARK : HLJS_LIGHT;
    let link = document.getElementById(HLJS_LINK_ID);
    if (!link) {
        link = document.createElement('link');
        link.rel = 'stylesheet';
        link.id  = HLJS_LINK_ID;
        document.head.appendChild(link);
    }
    if (link.href !== href) link.href = href;
}

/** Called once after hljs loads — watches for app theme changes. */
function _watchHljsTheme() {
    // ThemeSelector dispatches 'themeChanged' on window
    window.addEventListener('themeChanged', _applyHljsTheme);
    // Also observe the attribute directly (belt-and-suspenders)
    new MutationObserver(_applyHljsTheme).observe(document.documentElement, {
        attributes: true, attributeFilter: ['data-bs-theme'],
    });
}

async function loadHljs() {
    if (_hljs) return _hljs;
    if (_hljsLoading) return _hljsLoading;
    _hljsLoading = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/highlight.min.js';
        script.onload = () => {
            _hljs = window.hljs;
            _applyHljsTheme();  // inject correct CSS for current theme
            _watchHljsTheme();  // swap CSS on future theme changes
            resolve(_hljs);
        };
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
    });
    return _hljsLoading;
}


// ── Renderer registry — extend with FilePreviewModal.registerRenderer() ───────
const _renderers = new Map();

function _styleXlsxTable(container) {
    const table = container.querySelector('table');
    if (!table) return;
    table.style.cssText = 'border-collapse:collapse;font-size:var(--ab-text-sm);width:100%;';
    table.querySelectorAll('td,th').forEach(cell => {
        cell.style.cssText = 'border:1px solid var(--bs-border-color-translucent);padding:4px 8px;white-space:nowrap;';
    });
    table.querySelectorAll('tr:nth-child(even)').forEach(row => {
        row.style.background = 'var(--bs-tertiary-bg)';
    });
}

export class FilePreviewModal extends ComponentBase {
    /**
     * Register a custom renderer for additional file types.
     * renderFn(url, fileName, fetchContent) → Promise<HTMLElement|string>
     *   fetchContent() → Promise<string>  fetches raw text (cached, respects MAX_TEXT_BYTES)
     */
    static registerRenderer(category, extensions, renderFn) {
        _renderers.set(category, renderFn);
        for (const ext of extensions) EXT_MAP[ext] = category;
    }

    static styles = `
        .fpm-overlay {
            position: fixed; inset: 0; z-index: var(--ab-z-overlay);
            background: rgba(0,0,0,0.6); backdrop-filter: blur(4px);
            display: flex; align-items: center; justify-content: center;
            padding: 16px;
        }
        .fpm-modal {
            background: var(--bs-body-bg); border-radius: var(--ab-radius-lg);
            box-shadow: var(--ab-shadow-xl);
            display: flex; flex-direction: column;
            width: min(900px, 100%); height: min(85vh, 700px);
            overflow: hidden;
        }
        .fpm-header {
            display: flex; align-items: center; gap: 8px; padding: 10px 16px;
            border-bottom: 1px solid var(--bs-border-color-translucent); flex-shrink: 0;
        }
        .fpm-filename {
            flex: 1; font-weight: 600; font-size: var(--ab-text-base);
            overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        }
        .fpm-body { flex: 1; overflow: auto; position: relative; }
        .fpm-footer {
            padding: 6px 16px; font-size: var(--ab-text-xs); color: var(--bs-secondary-color);
            border-top: 1px solid var(--bs-border-color-translucent);
            display: flex; gap: 16px; flex-shrink: 0;
        }
        /* Content areas */
        .fpm-code-wrap { margin: 0; padding: 16px; overflow: auto; height: 100%;
            font-size: var(--ab-text-sm); line-height: 1.5; background: var(--bs-tertiary-bg); }
        .fpm-code-wrap pre { margin: 0; }
        .fpm-text-wrap { padding: 16px; font-size: var(--ab-text-sm); white-space: pre-wrap;
            word-break: break-word; font-family: var(--bs-font-monospace); line-height: 1.6; }
        .fpm-markdown-wrap { padding: 16px 24px; line-height: 1.7; }
        .fpm-image-wrap { display: flex; align-items: center; justify-content: center;
            height: 100%; padding: 16px; }
        .fpm-image-wrap img { max-width: 100%; max-height: 100%; object-fit: contain;
            border-radius: var(--ab-radius-xs); }
        .fpm-iframe { width: 100%; height: 100%; border: none; display: block; }
        .fpm-media-wrap { display: flex; align-items: center; justify-content: center;
            height: 100%; padding: 16px; }
        .fpm-media-wrap video, .fpm-media-wrap audio { max-width: 100%; max-height: 100%; }
        .fpm-no-preview { display: flex; flex-direction: column; align-items: center;
            justify-content: center; height: 100%; gap: 12px;
            color: var(--bs-secondary-color); text-align: center; padding: 24px; }
        .fpm-no-preview i { font-size: 3rem; opacity: 0.4; }
        .fpm-truncated {
            background: var(--bs-warning-bg-subtle); color: var(--bs-warning-text-emphasis);
            font-size: var(--ab-text-xs); padding: 6px 16px; text-align: center; flex-shrink: 0;
        }
        .fpm-loading { display: flex; align-items: center; justify-content: center;
            height: 100%; gap: 8px; color: var(--bs-secondary-color); }
        /* docx-preview overrides to match app theme */
        .docx-render { font-family: inherit !important; }
        .docx-render .docx { min-width: unset !important; padding: 0 !important; box-shadow: none !important; }

        /* Fullscreen on mobile */
        @media (max-width: 767px) {
            .fpm-overlay {
                padding: 0 !important;
            }
            .fpm-modal {
                width: 100vw !important;
                height: 100dvh !important;
                max-height: 100dvh !important;
                max-width: 100vw !important;
                border-radius: 0 !important;
            }
        }
    `;

    constructor(config) {
        super(config);
        this.workspace   = config.workspace;
        this.overlay     = null;
        this.threadId    = null;
        this.filePath    = null;
        this.fileName    = null;
        this.agentName   = null;
        this.spaceId     = null;
        this.projectId   = null;
        this.fileUrl     = null;
        this.truncated   = false;
        this._textCache  = null;
        this.applyStyles();

        this._boundHandlePreview = this._handlePreview.bind(this);
    }

    async initialize() {
        this.appClient.events.local.on('previewAgentFile', this._boundHandlePreview);
        await super.initialize();
    }

    _handlePreview({ threadId, filePath, fileName, projectId, agentName, spaceId }) {
        this.open(threadId, filePath, fileName, projectId, agentName, spaceId);
    }

    async open(threadId, filePath, fileName, projectId, agentName, spaceId) {
        this.threadId  = threadId;
        this.filePath  = filePath;
        this.fileName  = fileName;
        this.projectId = projectId;
        this.agentName = agentName;
        this.spaceId = spaceId;
        this._textCache = null;
        this.truncated  = false;

        this._createOverlay();
        this._renderLoading();

        try {
            const res = await this.appClient.rpcClient.call(
                'ThreadRpc.getAgentFileUrl',
                { threadId, agentName, spaceId, filePath },
                { projectId }
            );
            this.fileUrl = res?.url;
            await this._renderContent();
        } catch (err) {
            this.log.error('FilePreviewModal: failed to get URL', err);
            this._renderError(err.message);
        }
    }

    close() {
        if (this.overlay) {
            this.overlay.remove();
            this.overlay = null;
        }
    }

    async refresh() {
        if (!this.threadId) return;
        this._textCache = null;
        await this.open(this.threadId, this.filePath, this.fileName, this.projectId);
    }

    async download() {
        if (!this.fileUrl) return;
        const a = document.createElement('a');
        a.href = this.fileUrl;
        a.download = this.fileName;
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    async copyToClipboard() {
        const text = this._textCache;
        if (!text) return;
        const btn = this.overlay?.querySelector('#fpm-copy');
        try {
            await navigator.clipboard.writeText(text);
            if (btn) {
                btn.innerHTML = '<i class="bi bi-clipboard-check text-success"></i>';
                setTimeout(() => { btn.innerHTML = '<i class="bi bi-clipboard"></i>'; }, 1500);
            }
        } catch (_) {}
    }

    // ── Internal ──────────────────────────────────────────────────────────────

    _createOverlay() {
        if (this.overlay) this.overlay.remove();

        this.overlay = document.createElement('div');
        this.overlay.className = 'fpm-overlay';
        this.overlay.innerHTML = `
            <div class="fpm-modal" id="fpm-modal">
                <div class="fpm-header">
                    <i class="bi bi-file-earmark me-1 text-secondary" id="fpm-icon"></i>
                    <span class="fpm-filename" id="fpm-filename">${this.escapeHtml(this.fileName)}</span>
                    <button class="btn btn-sm btn-outline-secondary" id="fpm-refresh" title="Refresh">
                        <i class="bi bi-arrow-clockwise"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-secondary" id="fpm-copy" title="Copy to clipboard" style="display:none">
                        <i class="bi bi-clipboard"></i>
                    </button>
                    <button class="btn btn-sm btn-outline-primary" id="fpm-download" title="Download">
                        <i class="bi bi-download"></i> <span class="d-none d-md-inline">Download</span>
                    </button>
                    <button class="btn btn-sm btn-outline-danger" id="fpm-close" title="Close">
                        <i class="bi bi-x-lg"></i>
                    </button>
                </div>
                <div class="fpm-body" id="fpm-body"></div>
                <div class="fpm-footer" id="fpm-footer"></div>
            </div>
        `;

        // Close on backdrop click
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay) this.close();
        });
        this.overlay.querySelector('#fpm-close').addEventListener('click', () => this.close());
        this.overlay.querySelector('#fpm-download').addEventListener('click', () => this.download());
        this.overlay.querySelector('#fpm-refresh').addEventListener('click', () => this.refresh());
        this.overlay.querySelector('#fpm-copy').addEventListener('click', () => this.copyToClipboard());

        document.body.appendChild(this.overlay);
    }

    _renderLoading() {
        const body = this.overlay?.querySelector('#fpm-body');
        if (body) body.innerHTML = `<div class="fpm-loading">
            <div class="spinner-border spinner-border-sm"></div> Loading preview...
        </div>`;
    }

    _renderError(msg) {
        const body = this.overlay?.querySelector('#fpm-body');
        if (body) body.innerHTML = `<div class="fpm-no-preview">
            <i class="bi bi-exclamation-triangle"></i>
            <div>Could not load preview</div>
            <div class="small text-danger">${this.escapeHtml(msg)}</div>
        </div>`;
    }

    async _renderContent() {
        const body   = this.overlay?.querySelector('#fpm-body');
        const footer = this.overlay?.querySelector('#fpm-footer');
        if (!body || !this.fileUrl) return;

        const ext      = (this.fileName.split('.').pop() || '').toLowerCase();
        const category = EXT_MAP[ext] || 'binary';

        // Custom renderer registered externally
        if (_renderers.has(category)) {
            const el = await _renderers.get(category)(this.fileUrl, this.fileName, () => this._fetchText());
            body.innerHTML = '';
            if (typeof el === 'string') body.innerHTML = el;
            else body.appendChild(el);
            return;
        }

        // Built-in renderers
        const textCategories = new Set(['code', 'text', 'markdown']);
        const copyBtn = this.overlay?.querySelector('#fpm-copy');
        if (copyBtn) copyBtn.style.display = textCategories.has(category) ? '' : 'none';

        switch (category) {
            case 'code':    await this._renderCode(body, ext); break;
            case 'text':    await this._renderText(body); break;
            case 'markdown': await this._renderMarkdown(body); break;
            case 'image':   this._renderImage(body); break;
            case 'pdf':     this._renderPdf(body); break;
            case 'html':    this._renderIframe(body, true); break;
            case 'video':   this._renderVideo(body); break;
            case 'audio':   this._renderAudio(body); break;
            case 'docx':    await this._renderDocx(body); break;
            case 'xlsx':    await this._renderXlsx(body); break;
            case 'office-fallback': this._renderOfficeFallback(body, ext); break;
            default:        this._renderNoPreview(body); break;
        }

        if (footer) {
            footer.innerHTML = `<span>${this.escapeHtml(this.filePath)}</span>`;
            if (this.truncated) {
                const trunc = document.createElement('div');
                trunc.className = 'fpm-truncated';
                trunc.textContent = `Preview limited to first 512 KB. Download for full file.`;
                body.before(trunc);
            }
        }
    }

    async _fetchText() {
        if (this._textCache !== null) return this._textCache;
        // Fetch via server-side proxy to avoid CORS — filer HTTP has no CORS headers.
        // The server validates thread ownership and proxies the content.
        const res = await this.appClient.rpcClient.call(
            'ThreadRpc.getAgentFileContent',
            { threadId: this.threadId, agentName: this.agentName, spaceId: this.spaceId, filePath: this.filePath },
            { projectId: this.projectId }
        );
        if (res.truncated) this.truncated = true;
        this._textCache = res.text || '';
        return this._textCache;
    }

    async _renderCode(body, ext) {
        const text = await this._fetchText();
        const pre  = document.createElement('pre');
        const code = document.createElement('code');
        code.textContent = text;
        pre.appendChild(code);
        const wrap = document.createElement('div');
        wrap.className = 'fpm-code-wrap';
        wrap.appendChild(pre);
        body.innerHTML = '';
        body.appendChild(wrap);

        const hljs = await loadHljs();
        if (hljs) hljs.highlightElement(code);
    }

    async _renderText(body) {
        const text = await this._fetchText();
        const div  = document.createElement('div');
        div.className = 'fpm-text-wrap';
        div.textContent = text;
        body.innerHTML = '';
        body.appendChild(div);
    }

    async _renderMarkdown(body) {
        const text = await this._fetchText();
        const div  = document.createElement('div');
        div.className = 'fpm-markdown-wrap';
        div.innerHTML  = marked.parse(text);
        body.innerHTML = '';
        body.appendChild(div);
    }

    _renderImage(body) {
        body.innerHTML = `<div class="fpm-image-wrap">
            <img src="${this.escapeHtml(this.fileUrl)}" alt="${this.escapeHtml(this.fileName)}">
        </div>`;
    }

    // ── PDF — explicit method so we can append ?inline=1 for server-side inline disposition
    _renderPdf(body) {
        const url = this._inlineUrl(this.fileUrl);
        body.innerHTML = `<iframe class="fpm-iframe" src="${this.escapeHtml(url)}"></iframe>`;
    }

    _renderIframe(body, sandbox) {
        const attrs = sandbox ? 'sandbox="allow-scripts allow-same-origin"' : '';
        const url = this._inlineUrl(this.fileUrl);
        body.innerHTML = `<iframe class="fpm-iframe" src="${this.escapeHtml(url)}" ${attrs}></iframe>`;
    }

    /** Appends ?inline=1 to a download-key URL so the server sets Content-Disposition: inline */
    _inlineUrl(url) {
        if (!url) return url;
        try {
            const u = new URL(url, window.location.origin);
            u.searchParams.set('inline', '1');
            return u.toString();
        } catch (_) {
            return url + (url.includes('?') ? '&' : '?') + 'inline=1';
        }
    }

    _renderVideo(body) {
        body.innerHTML = `<div class="fpm-media-wrap">
            <video controls src="${this.escapeHtml(this.fileUrl)}"></video>
        </div>`;
    }

    _renderAudio(body) {
        body.innerHTML = `<div class="fpm-media-wrap">
            <audio controls src="${this.escapeHtml(this.fileUrl)}"></audio>
        </div>`;
    }

    async _renderDocx(body) {
        const ok = await loadDocxPreview();
        if (!ok || !window.docx) {
            this._renderNoPreview(body);
            return;
        }
        body.innerHTML = '';
        const wrap = document.createElement('div');
        wrap.className = 'docx-render-wrap';
        wrap.style.cssText = 'padding:16px;height:100%;overflow:auto;';
        body.appendChild(wrap);
        try {
            // fetch works correctly regardless of Content-Disposition header
            const res = await fetch(this._inlineUrl(this.fileUrl));
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const buf = await res.arrayBuffer();
            await window.docx.renderAsync(buf, wrap, null, {
                className: 'docx-render',
                inWrapper: false,
                ignoreLastRenderedPageBreak: true,
            });
        } catch (e) {
            body.innerHTML = `<div class="fpm-no-preview"><i class="bi bi-file-earmark-word"></i><div>Could not render document</div><div class="small text-danger">${e.message}</div><div class="small mt-2">Use the Download button to open in Word.</div></div>`;
        }
    }

    async _renderXlsx(body) {
        const ok = await loadSheetJS();
        if (!ok || !window.XLSX) {
            this._renderNoPreview(body);
            return;
        }
        body.innerHTML = '';
        try {
            const res  = await fetch(this._inlineUrl(this.fileUrl));
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const buf  = await res.arrayBuffer();
            const wb   = window.XLSX.read(buf, { type: 'array' });

            // Tab bar for multiple sheets
            const wrap = document.createElement('div');
            wrap.style.cssText = 'display:flex;flex-direction:column;height:100%;';

            if (wb.SheetNames.length > 1) {
                const tabs = document.createElement('div');
                tabs.style.cssText = 'display:flex;gap:4px;padding:8px 12px 0;border-bottom:1px solid var(--bs-border-color);flex-shrink:0;overflow-x:auto;';
                wb.SheetNames.forEach((name, i) => {
                    const btn = document.createElement('button');
                    btn.textContent = name;
                    btn.style.cssText = `padding:4px 12px;border:1px solid var(--bs-border-color);border-bottom:none;border-radius:4px 4px 0 0;background:${i===0?'var(--bs-body-bg)':'var(--bs-tertiary-bg)'};cursor:pointer;white-space:nowrap;font-size:var(--ab-text-sm);`;
                    btn.addEventListener('click', () => {
                        tableArea.innerHTML = window.XLSX.utils.sheet_to_html(wb.Sheets[name]);
                        _styleXlsxTable(tableArea);
                        tabs.querySelectorAll('button').forEach(b => b.style.background='var(--bs-tertiary-bg)');
                        btn.style.background = 'var(--bs-body-bg)';
                    });
                    tabs.appendChild(btn);
                });
                wrap.appendChild(tabs);
            }

            const tableArea = document.createElement('div');
            tableArea.style.cssText = 'flex:1;overflow:auto;padding:12px;';
            tableArea.innerHTML = window.XLSX.utils.sheet_to_html(wb.Sheets[wb.SheetNames[0]]);
            _styleXlsxTable(tableArea);
            wrap.appendChild(tableArea);
            body.appendChild(wrap);
        } catch (e) {
            body.innerHTML = `<div class="fpm-no-preview"><i class="bi bi-file-earmark-spreadsheet"></i><div>Could not render spreadsheet</div><div class="small text-danger">${e.message}</div></div>`;
        }
    }

    _renderNoPreview(body) {
        body.innerHTML = `<div class="fpm-no-preview">
            <i class="bi bi-file-earmark-x"></i>
            <div class="fw-medium">No preview available</div>
            <div class="small">Use the Download button to save this file.</div>
        </div>`;
    }

    /** Graceful fallback for unsupported office formats (.doc, .ppt, .pptx, .odp) */
    _renderOfficeFallback(body, ext) {
        const labels = {
            doc:  'Word 97-2003 Document (.doc)',
            ppt:  'PowerPoint 97-2003 Presentation (.ppt)',
            pptx: 'PowerPoint Presentation (.pptx)',
            odp:  'OpenDocument Presentation (.odp)',
        };
        const icons = {
            doc: 'bi-file-earmark-word',
            ppt: 'bi-file-earmark-slides', pptx: 'bi-file-earmark-slides', odp: 'bi-file-earmark-slides',
        };
        const label = labels[ext] || ext.toUpperCase();
        const icon  = icons[ext] || 'bi-file-earmark';
        body.innerHTML = `<div class="fpm-no-preview">
            <i class="bi ${icon}" style="font-size:3rem;opacity:0.4;"></i>
            <div class="fw-medium">${this.escapeHtml(label)}</div>
            <div class="small text-muted mt-1">Client-side preview is not available for this format.</div>
            <div class="small mt-1">Use the <strong>Download</strong> button to open in Microsoft Office or LibreOffice.</div>
        </div>`;
    }

    destroy() {
        this.appClient.events.local.off('previewAgentFile', this._boundHandlePreview);
        this.close();
        super.destroy?.();
    }
}
